import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Minus, Plus, ShieldCheck, ShoppingBag, Trash2, Truck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/format";
import PaymentBadges from "@/components/PaymentBadges";
import { useSeo } from "@/lib/seo";

export default function Cart() {
  const { items, updateQuantity, removeFromCart, subtotal, savings, currency, cartCount } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  useSeo({ title: "Mon panier — ISMO-TECH", noIndex: true });

  const goCheckout = () => {
    if (!user) {
      toast.error("Connectez-vous pour passer votre commande");
      navigate("/connexion", { state: { from: "/commander" } });
      return;
    }
    navigate("/commander");
  };

  if (!items.length) {
    return (
      <div className="container-it py-16 text-center lg:py-28" data-testid="cart-empty">
        <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-light text-brand">
          <ShoppingBag className="h-9 w-9" />
        </span>
        <h1 className="mt-6 font-display text-2xl font-extrabold text-ink">Votre panier est vide</h1>
        <p className="mt-2 text-sm text-slate-500">
          Parcourez notre catalogue de licences et retrouvez vos produits ici.
        </p>
        <Link
          to="/boutique"
          data-testid="cart-empty-shop-link"
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white"
        >
          Découvrir la boutique <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="container-it py-6 lg:py-12">
      <h1 className="font-display text-2xl font-extrabold text-ink sm:text-4xl" data-testid="cart-title">
        Mon panier
      </h1>
      <p className="mt-1.5 text-sm text-slate-500">
        {cartCount} article{cartCount > 1 ? "s" : ""} — livraison numérique instantanée
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_380px] lg:gap-10">
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.product_id}
              data-testid={`cart-item-${item.slug}`}
              className="flex gap-4 rounded-2xl border border-line bg-white p-4"
            >
              <Link to={`/boutique/${item.slug}`} className="shrink-0">
                <img
                  src={item.image_url}
                  alt={item.name}
                  loading="lazy"
                  className="h-24 w-24 rounded-xl border border-line bg-slate-50 object-contain p-2"
                />
              </Link>

              <div className="min-w-0 flex-1">
                <Link
                  to={`/boutique/${item.slug}`}
                  className="line-clamp-2 text-sm font-bold text-ink hover:text-brand sm:text-base"
                >
                  {item.name}
                </Link>
                <p className="mt-1 text-xs text-slate-500">
                  {item.license_type} · {item.duration}
                </p>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center rounded-full border border-line">
                    <button
                      data-testid={`cart-decrease-${item.slug}`}
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="flex h-9 w-9 items-center justify-center text-ink hover:text-brand"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-7 text-center text-sm font-bold" data-testid={`cart-qty-${item.slug}`}>
                      {item.quantity}
                    </span>
                    <button
                      data-testid={`cart-increase-${item.slug}`}
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="flex h-9 w-9 items-center justify-center text-ink hover:text-brand"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-display text-base font-extrabold text-ink">
                        {formatPrice(item.price * item.quantity, currency)}
                      </p>
                      {item.old_price > item.price && (
                        <p className="text-[11px] text-slate-400 line-through">
                          {formatPrice(item.old_price * item.quantity, currency)}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${item.slug}`}
                      aria-label="Retirer"
                      onClick={() => removeFromCart(item.product_id)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors duration-200 hover:bg-rose-50 hover:text-rose-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Link
            to="/boutique"
            className="inline-flex items-center gap-2 pt-2 text-sm font-bold text-brand hover:underline"
          >
            Continuer mes achats
          </Link>
        </div>

        <aside className="lg:sticky lg:top-[150px] lg:h-fit">
          <div className="rounded-2xl border border-line bg-white p-6">
            <p className="font-display text-lg font-bold text-ink">Récapitulatif</p>

            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Sous-total</dt>
                <dd className="font-semibold text-ink" data-testid="cart-subtotal">
                  {formatPrice(subtotal, currency)}
                </dd>
              </div>
              {savings > 0 && (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Réduction produits</dt>
                  <dd className="font-semibold text-emerald-600" data-testid="cart-savings">
                    −{formatPrice(savings, currency)}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-slate-500">Livraison numérique</dt>
                <dd className="font-semibold text-emerald-600">Gratuite</dd>
              </div>
              <div className="border-t border-line pt-3">
                <div className="flex items-baseline justify-between">
                  <dt className="font-bold text-ink">Total</dt>
                  <dd className="font-display text-2xl font-black text-ink" data-testid="cart-total">
                    {formatPrice(subtotal, currency)}
                  </dd>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Les éventuels frais Mobile Money s'affichent au paiement.
                </p>
              </div>
            </dl>

            <button
              type="button"
              onClick={goCheckout}
              data-testid="checkout-link"
              className="btn-glow mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95"
            >
              Passer la commande <ArrowRight className="h-4 w-4" />
            </button>

            <div className="mt-5 space-y-2.5 text-xs text-slate-500">
              <p className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand" /> Paiement sécurisé
              </p>
              <p className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-brand" /> Livraison numérique immédiate
              </p>
            </div>

            <div className="mt-5 border-t border-line pt-5">
              <PaymentBadges size="sm" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
